import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)") — a korábbi FIX
// szövegű ágak eldobták a szerver indoklását. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../utils/apiErrors';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Stack,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../utils/dateLocale';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/hu';
import { trainingBalanceService } from '../services/trainingBalanceService';
import { userService } from '../services/userService';
import { venueService } from '../services/venueService';
import type { TrainingBalance } from '../types/trainingBalance';
import type { UserListItem } from '../types/user';

/**
 * Az egyenleg-nézet alapértelmezett dátumtartománya: az elmúlt egy hónap a mai napig.
 * Kiemelve, hogy a „ne legyen fordított/múltbeli tartomány" invariáns tesztelhető legyen.
 */
export function getDefaultBalanceRange(): { startDate: Dayjs; endDate: Dayjs } {
  const endDate = dayjs();
  const startDate = endDate.subtract(1, 'month');
  return { startDate, endDate };
}

const AdminTrainingBalance: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [balances, setBalances] = useState<Map<string, TrainingBalance>>(new Map());
  const [venues, setVenues] = useState<any[]>([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [venueFilter, setVenueFilter] = useState<string>('');

  // Date range for balance period. Az endDate korábban fixen dayjs('2025-12-31') volt
  // → múltbeli és a startDate (1 hónapja) elé esett (fordított tartomány), így minden
  // játékos üres/0 egyenleget mutatott első betöltéskor. Most a mai naphoz kötött, nem
  // fordított tartomány (lásd getDefaultBalanceRange).
  const [startDate, setStartDate] = useState<Dayjs>(() => getDefaultBalanceRange().startDate);
  const [endDate, setEndDate] = useState<Dayjs>(() => getDefaultBalanceRange().endDate);

  // Load all users and venues on mount
  useEffect(() => {
    loadUsers();
    loadVenues();
  }, []);

  // Load balances when filtered users or dates change
  useEffect(() => {
    if (filteredUsers.length > 0) {
      loadAllBalances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchTerm, venueFilter, startDate, endDate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const result = await userService.getUsers();
      // Filter to only show players (non-admin, non-coach users)
      const players = result.data.filter(
        (user) => user.userRole !== 'Administrator' && user.userRole !== 'Coach'
      );
      setUsers(players);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Hiba történt a felhasználók betöltésekor'));
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadVenues = async () => {
    try {
      const venuesData = await venueService.getActiveVenues();
      setVenues(venuesData);
    } catch (err) {
      console.error('Error loading venues:', err);
    }
  };

  // Filter users based on search term and venue
  const filteredUsers = users
    .filter((user) => {
      const matchesSearch = !searchTerm ||
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // [C10, 2026-10-30] a típus szerint `null` is lehet (a szerver nem-adminnak más tag e-mailjét nem adja,
        // `backend/PinyoApi/Services/UserListSanitizer.cs`); ez a képernyő admin-only, ott mindig kitöltött —
        // a `?? ''` csak a null-on való összeomlás ellen véd, a viselkedés változatlan.
        (user.email ?? '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesVenue = !venueFilter || user.venueId === venueFilter;
      return matchesSearch && matchesVenue;
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'hu'));

  const clearFilters = () => {
    setSearchTerm('');
    setVenueFilter('');
  };

  const loadAllBalances = async () => {
    try {
      setLoading(true);
      setError('');

      const balanceMap = new Map<string, TrainingBalance>();

      // Use batch endpoint for better performance (single HTTP request instead of 400+)
      const userIds = filteredUsers.map(user => user.userId);

      if (userIds.length === 0) {
        setBalances(balanceMap);
        return;
      }

      console.log(`Loading balances for ${userIds.length} users using batch endpoint...`);

      const balances = await trainingBalanceService.getBatchBalances(userIds, {
        startDate: startDate.toDate(),
        endDate: endDate.toDate(),
      });

      // Convert array to map
      balances.forEach(balance => {
        balanceMap.set(balance.userId, balance);
      });

      console.log(`Successfully loaded ${balances.length} balances`);
      setBalances(balanceMap);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Hiba történt az egyenlegek betöltésekor'));
      console.error('Error loading balances:', err);
    } finally {
      setLoading(false);
    }
  };

  const setQuickPeriod = (months: number) => {
    const end = dayjs();
    const start = dayjs().subtract(months, 'month');
    setStartDate(start);
    setEndDate(end);
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    let totalPlanned = 0;
    let totalAttended = 0;
    let totalCancelled = 0;
    let totalMakeup = 0;

    balances.forEach((balance) => {
      totalPlanned += balance.plannedTrainings;
      totalAttended += balance.attendedTrainings;
      totalCancelled += balance.cancelledTrainings;
      totalMakeup += balance.availableMakeupCredits;
    });

    const avgAttendance = totalPlanned > 0 ? (totalAttended / totalPlanned) * 100 : 0;

    return {
      totalPlanned,
      totalAttended,
      totalCancelled,
      totalMakeup,
      avgAttendance,
    };
  };

  const summary = calculateSummary();

  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="hu" localeText={HU_PICKER_LOCALE_TEXT}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Játékosok Edzés Egyenlege
        </Typography>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Szűrők
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              size="small"
              label="Keresés (név vagy email)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: { sm: 250 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Helyszín</InputLabel>
              <Select
                value={venueFilter}
                onChange={(e) => setVenueFilter(e.target.value)}
                label="Helyszín"
              >
                <MenuItem value="">Összes helyszín</MenuItem>
                {venues.map((venue) => (
                  <MenuItem key={venue.venueId} value={venue.venueId}>
                    {venue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
            >
              Szűrők törlése
            </Button>
          </Stack>
        </Paper>

        {/* Date Range Selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Időszak kiválasztása
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <DatePicker
              label="Kezdő dátum"
              value={startDate}
              onChange={(value) => {
                if (value && dayjs.isDayjs(value) && value.isValid()) {
                  setStartDate(value);
                }
              }}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="Záró dátum"
              value={endDate}
              onChange={(value) => {
                if (value && dayjs.isDayjs(value) && value.isValid()) {
                  setEndDate(value);
                }
              }}
              slotProps={{ textField: { size: 'small' } }}
            />
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={() => setQuickPeriod(1)}>
                1 Hónap
              </Button>
              <Button size="small" variant="outlined" onClick={() => setQuickPeriod(3)}>
                3 Hónap
              </Button>
              <Button size="small" variant="outlined" onClick={() => setQuickPeriod(6)}>
                6 Hónap
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Össz. tervezett
              </Typography>
              <Typography variant="h4">{summary.totalPlanned}</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Össz. részvétel
              </Typography>
              <Typography variant="h4" color="success.main">
                {summary.totalAttended}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Össz. lemondás
              </Typography>
              <Typography variant="h4" color="warning.main">
                {summary.totalCancelled}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Átlag részvétel
              </Typography>
              <Typography variant="h4" color="primary.main">
                {summary.avgAttendance.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Balance Table */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Játékos</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Tervezett
                </TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Részt vett
                </TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Lemondott
                </TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Pótlási kredit
                </TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Hátralévő
                </TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Részvétel %
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={30} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Nincs megjeleníthető játékos
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => {
                  const balance = balances.get(user.userId);
                  if (!balance) {
                    return (
                      <TableRow key={user.userId}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary">
                            Adatok betöltése...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={user.userId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {user.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={balance.plannedTrainings}
                          size="small"
                          color="default"
                          sx={{ minWidth: 50 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={balance.attendedTrainings}
                          size="small"
                          color="success"
                          sx={{ minWidth: 50 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={balance.cancelledTrainings}
                          size="small"
                          color="warning"
                          sx={{ minWidth: 50 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={balance.availableMakeupCredits}
                          size="small"
                          color={balance.availableMakeupCredits > 0 ? 'info' : 'default'}
                          sx={{ minWidth: 50 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={balance.remainingPlannedTrainings}
                          size="small"
                          color="default"
                          sx={{ minWidth: 50 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${balance.attendancePercentage.toFixed(1)}%`}
                          size="small"
                          color={
                            balance.attendancePercentage >= 80
                              ? 'success'
                              : balance.attendancePercentage >= 60
                              ? 'warning'
                              : 'error'
                          }
                          sx={{ minWidth: 60 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminTrainingBalance;
